import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import QuickActionButton from '../../components/ui/QuickActionButton';
import ActivityFilters from './components/ActivityFilters';
import ActivityChart from './components/ActivityChart';
import ActivityTable from './components/ActivityTable';
import ActivityTableSkeleton from './components/ActivityTableSkeleton';
import ErrorRetryCard from './components/ErrorRetryCard';
import StepCounter from './components/StepCounter';
import QuickAddButtons from './components/QuickAddButtons';
import AddActivityModal from './components/AddActivityModal';
import PersonalRecordsPanel from './components/PersonalRecordsPanel';
import ProgressiveOverloadTracker from './components/ProgressiveOverloadTracker';
import WorkoutStopwatch from './components/WorkoutStopwatch';
import WorkoutRoutinesGuide from './components/WorkoutRoutinesGuide';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import PageTransition from '../../components/PageTransition';
import { useAuth } from '../../contexts/AuthContext';
import { workoutService } from '../../services/trackingService';
import { getFitnessActivities, importStravaActivitiesToLogs, getStravaConnection } from '../../services/stravaService';
import { useDashboardMetrics } from '../../hooks/useRealtimeMetrics';
import { useWorkoutLogsQuery } from '../../hooks/useWorkoutLogsQuery';
import milestoneService from '../../services/milestoneService';
import MilestonePopup from '../../components/MilestonePopup';
import { deduplicateAndMerge } from '../../utils/activityDuplicateDetector';

const TABS = [
  { id: 'activities', label: 'Activities', icon: 'Activity' },
  { id: 'stopwatch', label: 'Stopwatch', icon: 'Timer' },
  { id: 'routines', label: 'Routines Guide', icon: 'BookOpen' },
  { id: 'records', label: 'Personal Records', icon: 'Trophy' },
  { id: 'overload', label: 'Progressive Overload', icon: 'TrendingUp' },
];

const ActivityTracking = () => {
  const { user } = useAuth();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [currentSteps, setCurrentSteps] = useState(0);
  const [dailyGoal, setDailyGoal] = useState(10000);
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [stepSyncStatus, setStepSyncStatus] = useState('synced');
  const [fitnessActivities, setFitnessActivities] = useState([]);
  const [fitnessLoading, setFitnessLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('activities');
  const [prRefreshKey, setPrRefreshKey] = useState(0);
  const [prefillData, setPrefillData] = useState(null);
  const [stravaImportStatus, setStravaImportStatus] = useState(null); // null | 'importing' | { imported, skipped }

  // Milestone state
  const [currentMilestone, setCurrentMilestone] = useState(null);
  const [showMilestonePopup, setShowMilestonePopup] = useState(false);

  // React Query: paginated + cached workout logs
  const {
    activities,
    allActivities,
    totalCount,
    hasNextPage,
    loading,
    isFetching,
    error,
    refetch: refetchWorkouts,
    pageSize,
  } = useWorkoutLogsQuery(user?.id, currentPage, filters);

  // Auto-import Strava activities into workout_logs on mount (once per session)
  useEffect(() => {
    if (!user?.id) return;
    const sessionKey = `strava_import_done_${user?.id}`;
    if (sessionStorage.getItem(sessionKey)) return;

    const runImport = async () => {
      try {
        const connection = await getStravaConnection(user?.id);
        if (!connection) return; // No Strava connected
        setStravaImportStatus('importing');
        const result = await importStravaActivitiesToLogs(user?.id);
        setStravaImportStatus(result);
        if (result?.imported > 0) {
          refetchWorkouts();
        }
        sessionStorage.setItem(sessionKey, '1');
      } catch (err) {
        console.error('[Strava Auto-Import] Error:', err);
        setStravaImportStatus(null);
      }
    };
    runImport();
  }, [user?.id]);

  // Fetch fitness_activities (Strava + other providers)
  useEffect(() => {
    if (!user?.id) return;
    const loadFitnessActivities = async () => {
      setFitnessLoading(true);
      try {
        const data = await getFitnessActivities(user?.id, {
          provider: filters?.provider || undefined,
          activityType: filters?.activityType || undefined,
          startDate: (() => {
            if (!filters?.dateRange) return undefined;
            const today = new Date();
            if (filters?.dateRange === 'today') return today?.toISOString()?.split('T')?.[0];
            if (filters?.dateRange === 'week') {
              const d = new Date(today); d?.setDate(today?.getDate() - 7);
              return d?.toISOString()?.split('T')?.[0];
            }
            if (filters?.dateRange === 'month') {
              return new Date(today.getFullYear(), today.getMonth(), 1)?.toISOString()?.split('T')?.[0];
            }
            return undefined;
          })(),
        });
        setFitnessActivities(data);
      } catch (err) {
        console.error('Error loading fitness activities:', err);
        setFitnessActivities([]);
      } finally {
        setFitnessLoading(false);
      }
    };
    loadFitnessActivities();
  }, [user?.id, filters?.provider, filters?.activityType, filters?.dateRange]);

  // Real-time metrics (cached via React Query)
  const { metrics, refetch: refetchMetrics } = useDashboardMetrics(user?.id);

  // Update currentSteps from cached metrics
  useEffect(() => {
    if (metrics?.steps) {
      setCurrentSteps(metrics?.steps?.current);
      setDailyGoal(metrics?.steps?.target);
    }
  }, [metrics]);

  // Check for step milestones
  useEffect(() => {
    if (!user?.id || !metrics?.steps) return;
    const milestone = milestoneService?.checkMilestone(user?.id, 'steps', metrics?.steps?.current);
    if (milestone) {
      setCurrentMilestone(milestone);
      setShowMilestonePopup(true);
      milestoneService?.createMilestoneNotification(user?.id, milestone);
    }
  }, [metrics?.steps?.current, user?.id]);

  // Reset to page 1 when filters change
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleSaveActivity = async (activityData) => {
    if (!user?.id) return;
    try {
      setStepSyncStatus('syncing');
      if (editingActivity) {
        await workoutService?.updateWorkout(editingActivity?.id, {
          workout_name: activityData?.name,
          exercise_name: activityData?.name,
          workout_type: activityData?.type,
          completed_sets: activityData?.duration || 0,
          duration_minutes: activityData?.duration || 0,
          calories_burned: activityData?.calories || 0,
          steps: activityData?.steps || 0,
          intensity_level: activityData?.intensity,
          notes: activityData?.notes,
          workout_date: activityData?.date,
        });
        setEditingActivity(null);
      } else {
        await workoutService?.createWorkout(user?.id, {
          name: activityData?.name,
          type: activityData?.type,
          completedSets: activityData?.duration || 0,
          duration: activityData?.duration || 0,
          calories: activityData?.calories || 0,
          steps: activityData?.steps || 0,
          intensity: activityData?.intensity,
          notes: activityData?.notes,
          date: activityData?.date,
        });
      }
      // Invalidate both caches
      refetchWorkouts();
      refetchMetrics();
      setStepSyncStatus('synced');
      setIsAddModalOpen(false);
    } catch (err) {
      console.error('Error saving activity:', err);
      setStepSyncStatus('error');
      alert('Error al guardar la actividad. Por favor intenta de nuevo.');
    }
  };

  const handleEditActivity = (activity) => {
    setEditingActivity(activity);
    setIsAddModalOpen(true);
  };

  const handleDeleteActivity = async (activityId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta actividad?')) return;
    try {
      await workoutService?.deleteWorkout(activityId);
      refetchWorkouts();
      refetchMetrics();
    } catch (err) {
      console.error('Error deleting activity:', err);
      alert('Error al eliminar la actividad. Por favor intenta de nuevo.');
    }
  };

  const handleQuickAdd = async (activityData) => {
    if (!user?.id) return;
    try {
      setStepSyncStatus('syncing');
      await workoutService?.createWorkout(user?.id, {
        name: activityData?.name,
        type: activityData?.type,
        completedSets: activityData?.duration || 0,
        duration: activityData?.duration || 0,
        calories: activityData?.calories || 0,
        steps: activityData?.steps || 0,
        intensity: activityData?.intensity || 'moderate',
        notes: activityData?.notes || '',
        date: activityData?.date,
      });
      refetchWorkouts();
      refetchMetrics();
      setStepSyncStatus('synced');
    } catch (err) {
      console.error('Error adding quick activity:', err);
      setStepSyncStatus('error');
      alert('Error al agregar la actividad rápida. Por favor intenta de nuevo.');
    }
  };

  const handleGoalUpdate = (newGoal) => {
    setDailyGoal(newGoal);
  };

  const handleStepsReset = async () => {
    if (!user?.id) return;
    try {
      setStepSyncStatus('syncing');
      const today = new Date()?.toISOString()?.split('T')?.[0];
      const todayWorkouts = await workoutService?.getWorkouts(user?.id, today, today);
      for (const workout of todayWorkouts) {
        if (workout?.steps > 0) {
          await workoutService?.deleteWorkout(workout?.id);
        }
      }
      refetchWorkouts();
      refetchMetrics();
      setStepSyncStatus('synced');
    } catch (err) {
      console.error('Error resetting steps:', err);
      setStepSyncStatus('error');
      alert('Error al reiniciar los pasos. Por favor intenta de nuevo.');
    }
  };

  const handleAddActivity = () => {
    setEditingActivity(null);
    setPrefillData(null);
    setIsAddModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsAddModalOpen(false);
    setEditingActivity(null);
    setPrefillData(null);
  };

  // Called when a new PR is broken in the overload tracker
  const handleNewPR = (exerciseName, brokenTypes) => {
    setPrRefreshKey((k) => k + 1);
  };

  // Called when stopwatch session is saved — auto-populate the Add Activity modal
  const handleStopwatchSessionComplete = (sessionData) => {
    setPrefillData(sessionData);
    setEditingActivity(null);
    setIsAddModalOpen(true);
    refetchWorkouts();
    refetchMetrics();
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  // Merge manual activities with fitness_activities
  const mergedActivities = (() => {
    const providerFilter = filters?.provider;
    if (providerFilter === 'manual') return activities || [];
    if (providerFilter && providerFilter !== 'manual') return fitnessActivities;

    let filteredFitness = fitnessActivities;
    if (filters?.searchQuery) {
      const q = filters?.searchQuery?.toLowerCase();
      filteredFitness = fitnessActivities?.filter(a =>
        a?.activity_type?.toLowerCase()?.includes(q) ||
        a?.name?.toLowerCase()?.includes(q)
      );
    }

    let filteredManual = activities || [];
    if (filters?.searchQuery) {
      const q = filters?.searchQuery?.toLowerCase();
      filteredManual = filteredManual?.filter(a =>
        a?.workout_type?.toLowerCase()?.includes(q) ||
        a?.type?.toLowerCase()?.includes(q) ||
        a?.workout_name?.toLowerCase()?.includes(q) ||
        a?.name?.toLowerCase()?.includes(q)
      );
    }

    const deduped = deduplicateAndMerge(filteredFitness, filteredManual);

    return deduped?.sort((a, b) => {
      const dateA = new Date(a.date || a.start_time);
      const dateB = new Date(b.date || b.start_time);
      return dateB - dateA;
    });
  })();

  const mergedTotal = filters?.provider === 'manual'
    ? totalCount
    : filters?.provider
      ? fitnessActivities?.length
      : totalCount + fitnessActivities?.length;

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Activity Tracking - PrimeOne+</title>
        <meta name="description" content="Track your physical activities, monitor steps, and analyze your fitness progress with comprehensive activity logging and visualization tools." />
      </Helmet>
      <Header />
      <PageTransition>
        <main className="container mx-auto px-4 py-8 space-y-8">
          {/* Page Header */}
          <motion.div
            className="flex items-center justify-between"
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
          >
            <div className="flex items-center space-x-4">
              <motion.div
                className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-xl"
                whileHover={{ scale: 1.08, rotate: 5 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <Icon name="Activity" size={24} className="text-primary" />
              </motion.div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">Activity Tracking</h1>
                <p className="text-muted-foreground">
                  Monitor your physical activities and track your fitness progress
                </p>
              </div>
            </div>
            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              <Button
                variant="default"
                onClick={handleAddActivity}
                iconName="Plus"
                iconPosition="left"
                className="shadow-wellness"
              >
                Add Activity
              </Button>
            </motion.div>
          </motion.div>

          {/* Tab Navigation */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.05 }}
            className="flex gap-1 bg-muted/30 p-1 rounded-xl border border-border/40 w-fit overflow-x-auto"
          >
            {TABS?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  activeTab === tab?.id
                    ? 'bg-card text-foreground shadow-sm border border-border/40'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={15} className={activeTab === tab?.id ? (tab?.id === 'records' ? 'text-yellow-500' : tab?.id === 'stopwatch' ? 'text-primary' : 'text-primary') : ''} />
                {tab?.label}
              </button>
            ))}
          </motion.div>

          {/* ── ACTIVITIES TAB ── */}
          {activeTab === 'activities' && (
            <>
              {/* Strava Import Status Banner */}
              {stravaImportStatus === 'importing' && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-3 bg-orange-500/10 border border-orange-500/20 rounded-xl px-4 py-3 text-sm text-orange-600 dark:text-orange-400"
                >
                  <div className="w-4 h-4 border-2 border-orange-500/40 border-t-orange-500 rounded-full animate-spin flex-shrink-0" />
                  <span>Importing Strava workouts into Activity Logs…</span>
                </motion.div>
              )}
              {stravaImportStatus?.imported > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center gap-3 bg-green-500/10 border border-green-500/20 rounded-xl px-4 py-3 text-sm text-green-700 dark:text-green-400"
                >
                  <Icon name="CheckCircle" size={16} className="text-green-500 flex-shrink-0" />
                  <span>
                    <strong>{stravaImportStatus?.imported}</strong> Strava workout{stravaImportStatus?.imported !== 1 ? 's' : ''} automatically added to your Activity Logs.
                  </span>
                  <button
                    onClick={() => setStravaImportStatus(null)}
                    className="ml-auto text-green-600 hover:text-green-800 dark:hover:text-green-200"
                  >
                    <Icon name="X" size={14} />
                  </button>
                </motion.div>
              )}

              {/* Top Row - Step Counter and Quick Add */}
              <motion.div
                className="grid grid-cols-1 lg:grid-cols-2 gap-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1, ease: [0.4, 0, 0.2, 1] }}
              >
                <StepCounter
                  currentSteps={currentSteps}
                  dailyGoal={dailyGoal}
                  onGoalUpdate={handleGoalUpdate}
                  onReset={handleStepsReset}
                  syncStatus={stepSyncStatus}
                />
                <QuickAddButtons onQuickAdd={handleQuickAdd} />
              </motion.div>

              {/* Activity Chart */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.2, ease: [0.4, 0, 0.2, 1] }}
              >
                <ActivityChart allActivities={allActivities} />
              </motion.div>

              {/* Filters */}
              {!loading && !error && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3, delay: 0.25 }}
                >
                  <ActivityFilters
                    onFiltersChange={handleFiltersChange}
                    totalActivities={mergedTotal}
                  />
                </motion.div>
              )}

              {/* Loading State */}
              {(loading || fitnessLoading) && <ActivityTableSkeleton />}

              {/* Fetching indicator */}
              {!loading && !fitnessLoading && isFetching && (
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-2">
                  <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                  <span>Refreshing...</span>
                </div>
              )}

              {/* Error State */}
              {error && !loading && (
                <ErrorRetryCard
                  title="Failed to load workout logs"
                  message={error}
                  onRetry={refetchWorkouts}
                  icon="Activity"
                />
              )}

              {/* Activity Table */}
              {!loading && !fitnessLoading && !error && mergedActivities && mergedActivities?.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.3, ease: [0.4, 0, 0.2, 1] }}
                >
                  <ActivityTable
                    activities={mergedActivities}
                    onEditActivity={handleEditActivity}
                    onDeleteActivity={handleDeleteActivity}
                  />
                </motion.div>
              )}

              {/* Pagination */}
              {!loading && !fitnessLoading && !error && !filters?.provider && totalPages > 1 && (
                <div className="flex items-center justify-between bg-card rounded-xl px-6 py-4 shadow-wellness border border-border/40">
                  <p className="text-sm text-muted-foreground">
                    Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, totalCount)} of {totalCount} manual activities
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      iconName="ChevronLeft"
                      iconPosition="left"
                    >
                      Previous
                    </Button>
                    <span className="text-sm font-medium text-foreground px-2">
                      {currentPage} / {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => p + 1)}
                      disabled={!hasNextPage}
                      iconName="ChevronRight"
                      iconPosition="right"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}

              {/* Empty State - no results after filter */}
              {!loading && !fitnessLoading && !error && mergedActivities?.length === 0 && mergedTotal > 0 && (
                <motion.div
                  className="bg-card rounded-xl p-12 text-center shadow-wellness elevation-1 border border-border/40"
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="flex items-center justify-center w-16 h-16 bg-muted/50 rounded-full mx-auto mb-4">
                    <Icon name="Search" size={32} className="text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">No activities found</h3>
                  <p className="text-muted-foreground mb-6">Try adjusting your filters to see more activities</p>
                  <Button variant="outline" onClick={() => handleFiltersChange({})} iconName="RotateCcw" iconPosition="left">
                    Clear Filters
                  </Button>
                </motion.div>
              )}

              {/* No Activities State */}
              {!loading && !fitnessLoading && !error && mergedTotal === 0 && mergedActivities?.length === 0 && (
                <motion.div
                  className="bg-card rounded-xl p-12 text-center shadow-wellness elevation-1 border border-border/40"
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mx-auto mb-4">
                    <Icon name="Activity" size={32} className="text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">Start tracking your activities</h3>
                  <p className="text-muted-foreground mb-6">Log your first activity to begin monitoring your fitness journey</p>
                  <Button variant="default" onClick={handleAddActivity} iconName="Plus" iconPosition="left">
                    Add Your First Activity
                  </Button>
                </motion.div>
              )}
            </>
          )}

          {/* ── STOPWATCH TAB ── */}
          {activeTab === 'stopwatch' && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              className="max-w-2xl mx-auto space-y-6"
            >
              <WorkoutStopwatch
                userId={user?.id}
                onSessionComplete={handleStopwatchSessionComplete}
              />
            </motion.div>
          )}

          {/* ── ROUTINES GUIDE TAB ── */}
          {activeTab === 'routines' && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              className="max-w-3xl mx-auto"
            >
              <WorkoutRoutinesGuide selectedType={null} onClose={() => {}} />
            </motion.div>
          )}

          {/* ── PERSONAL RECORDS TAB ── */}
          {activeTab === 'records' && (
            <motion.div
              key={`records-${prRefreshKey}`}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
            >
              <PersonalRecordsPanel userId={user?.id} />
            </motion.div>
          )}

          {/* ── PROGRESSIVE OVERLOAD TAB ── */}
          {activeTab === 'overload' && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
            >
              <ProgressiveOverloadTracker userId={user?.id} onNewPR={handleNewPR} />
            </motion.div>
          )}
        </main>
      </PageTransition>
      <AddActivityModal
        isOpen={isAddModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveActivity}
        editingActivity={editingActivity}
        prefillData={prefillData}
      />
      <QuickActionButton />
      <MilestonePopup
        milestone={currentMilestone}
        isOpen={showMilestonePopup}
        onClose={() => setShowMilestonePopup(false)}
      />
    </div>
  );
};

export default ActivityTracking;